import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Prevents full black-screen death on render errors (e.g. broken HMR).
 * Stale HMR can leave this boundary stuck until a full document reload.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('UI error:', error, info.componentStack);
  }

  private hardReload = () => {
    // Full document reload (not SPA navigate) so fixed modules replace the
    // failed tree. Cache-bust in case a stale HMR/dev bundle is stuck.
    const url = new URL(window.location.href);
    url.searchParams.set('_r', String(Date.now()));
    window.location.replace(url.toString());
  };

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            minHeight: '100vh',
            padding: '2rem',
            background: '#0A0A0A',
            color: '#F5F5F7',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>
            Midagi läks valesti
          </h1>
          <p style={{ color: '#A1A1A6', marginBottom: '1.5rem' }}>
            {this.state.error.message}
          </p>
          <button
            type="button"
            onClick={this.hardReload}
            style={{
              minHeight: 44,
              padding: '0.6rem 1.25rem',
              background: '#F5F5F7',
              color: '#0A0A0A',
              border: 'none',
              borderRadius: 4,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Laadi leht uuesti
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
